import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { first } from 'rxjs';

/**
 * Guard de acceso a las rutas de administracion.
 *
 * Se suscribe al usuario autenticado de Firebase y comprueba su rol leyendo el
 * documento correspondiente de la coleccion `administrators` de Firestore.
 *
 * A diferencia del guard anterior, no consulta ningun flag guardado en memoria
 * (BehaviorSubject, propiedad de un servicio o claim cacheado): esos valores
 * viven en el navegador y pueden reescribirse desde la consola para simular ser
 * administrador. Aqui la respuesta la da Firestore en cada intento de acceso,
 * de modo que forzar una variable local no concede acceso.
 *
 * Si la comprobacion falla por cualquier motivo se deniega el acceso
 * (fail-closed) y se redirige mediante UrlTree, que el router aplica de forma
 * atomica.
 *
 * Nota: esto controla la navegacion de la SPA, que es codigo que el usuario
 * controla. Es una barrera de interfaz, no de datos: cada endpoint del backend
 * debe seguir validando el rol por su cuenta.
 */
@Injectable({
  providedIn: 'root',
})
export class AdminGuard implements CanActivate {
  constructor(
    private afAuth: AngularFireAuth,
    private firestore: AngularFirestore,
    private router: Router,
  ) {}

  async canActivate(): Promise<boolean | UrlTree> {
    const user = await this.afAuth.authState.pipe(first()).toPromise();

    if (!user) {
      return this.router.createUrlTree(['/login']);
    }

    try {
      const adminDoc = await this.firestore
        .collection('administrators')
        .doc(user.uid)
        .get()
        .toPromise();

      if (adminDoc?.exists && (adminDoc.data() as any)?.admin === true) {
        return true;
      }
    } catch (error) {
      // Fail-closed: si no se puede verificar el rol, no se concede acceso.
      console.error('[AdminGuard] No se pudo verificar el rol de administrador');
    }

    return this.router.createUrlTree(['/oportunidades']);
  }
}
